import { Button, useTheme } from '@material-ui/core'
import times from 'lodash.times'
import React from 'react'

import { updateCareerSkill, updateSkillRanks } from '../../api/character'
import { DIE_TYPES, ICON_BACKGROUNDS, SKILLS } from '../../constants'
import { usePaperExpand } from './util'

const GENERAL = [
  0, 1, 3, 4, 5, 6, 7, 9, 10, 13, 16, 17, 19, 21, 22, 23, 26, 27, 28, 29, 30, 32
]

const COMBAT = [
  2, 12, 14, 18, 24, 25
]

const KNOWLEDGE = [
  8, 11, 15, 20, 31, 33, 34
]

const SKILL_KEYS = Object.keys(SKILLS).sort()

const AGILITY_SKILLS = [
  6, 14, 15, 18, 23, 26, 27
]
const BRAWN_SKILLS = [
  1, 16, 22, 24, 25
]
const CUNNING_SKILLS = [
  7, 13, 17, 19, 20
]
const INT_SKILLS = [
  0, 4, 10, 11, 28, 29, 30, 31, 32, 33, 34
]
const PRESENCE_SKILLS = [
  2, 5, 9, 12
]
const WILLPOWER_SKILLS = [
  3, 8, 21
]

const DD = ({ num }) => {
  const el = ICON_BACKGROUNDS[DIE_TYPES.difficulty]
  return (
    <div className='diff' css={`display: flex; justify-content: center;`}>
      {times(num).map((_, idx) => <el.component height={18} width={18} key={idx} />)}
    </div>
  )
}

const buttons = [
  'Simple', 'Easy', 'Average', 'Hard', 'Daunting', 'Formidable'
]

const DifficultySelect = React.forwardRef(({ ability, proficiency, isOpen, onClick, skillName }, ref) => {
  const theme = useTheme()
  const diff = ICON_BACKGROUNDS[DIE_TYPES.difficulty]
  const ab = ICON_BACKGROUNDS[DIE_TYPES.ability]
  const prof = ICON_BACKGROUNDS[DIE_TYPES.proficiency]

  return !isOpen ? false : (
    <div ref={ref} css={`
      max-width: 242px;
      position: absolute;
      top: -1px;
      left: -8px;
      background-color: ${theme.palette.background.default};
      border-radius: 4px;
      padding: 8px;
      z-index: 1;
      text-align: left;
      box-shadow: 0px 0px 26px -5px ${theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0,0,0,0.75)'};

      .diff #fill {
        fill: ${diff.fill};
      }

      svg {
        margin-left: -3px;
        margin-right: -3px;
      }

      .prof {
        margin: 0;
      }
    `}>
      <div css={`display: flex; justify-content: space-between;`}>
        <div css={`text-transform: capitalize; font-weight: bold; line-height: 1.1;`}>
          {skillName}
        </div>
        <div css={`display: flex; align-items: center;`}>
          {proficiency > 0 && times(proficiency).map((_, idx) => <prof.component className='prof' key={idx} height={15} width={15} />)}
          {ability > 0 && times(ability).map((_, idx) => <ab.component key={idx} height={17} width={17} />)}
        </div>
      </div>
      <div css={`margin-top: 4px; display: flex; flex-direction: column;`}>
        <div css={`
          display: flex;
          flex-wrap: wrap;
          margin-left: -2px;

          button {
            width: 72px;
            margin: 2px;
            padding: 4px;
            font-size: 11px;
            text-align: center;

            .container {
              margin-top: -3px;
              display: flex;
              flex-direction: column;

              .small {
                font-size: 10px;
              }
            }
          }
        `}>
          {buttons.map((label, idx) => {
            return (
              <Button variant='outlined' key={label} onClick={onClick.bind(this, idx)}>
                <div className='container'>
                  <div className={idx === 5 ? 'small' : ''}>{label}</div>
                  {idx === 0 ?
                    <div> - </div>
                    :
                    <DD num={idx} />
                  }
                </div>
              </Button>
            )
          })}
        </div>
      </div>
    </div>
  )
})

const Skill = ({ characteristic, isCareer, onCareerSkill, onDicePool, onSkillRanks, ranks, skillIndex, skillName }) => {
  const modalRef = React.useRef(null)
  const [ isSelected, setIsSelected ] = React.useState(false)

  React.useEffect(() => {
    const handler = (e) => {
      const path = e.path || (e.composedPath && e.composedPath())

      if (!path.includes(modalRef.current)) {
        setIsSelected(false)
        e.stopImmediatePropagation()
      }
    }

    if (isSelected) {
      document.getElementById('root').addEventListener('click', handler)
    }

    return () => {
      document.getElementById('root').removeEventListener('click', handler)
    }
  }, [ isSelected ])

  const charType = SKILLS[skillName]
  const proficiency = Math.min(characteristic, ranks)
  const ability = Math.abs(characteristic - ranks)
  const isValidSkill = !isNaN(proficiency) && !isNaN(ability)

  const onDifficultySelect = (difficulty) => {
    setIsSelected(false)
    onDicePool({ proficiency, ability, difficulty }, skillIndex)
  }

  return (
    <div className='row' key={skillName}>
      <DifficultySelect
        ability={ability}
        proficiency={proficiency}
        ref={modalRef}
        isOpen={isSelected}
        onClick={onDifficultySelect}
        skillName={`${skillName} (${charType})`} />
      <div className={`name${isValidSkill ? ' valid' : ''}`}>
        <span
          className='skill-button'
          title={!isValidSkill ? 'No characteristics set' : 'Select difficulty and populate the dice pool'}
          onClick={() => {
            if (!isValidSkill) return
            setIsSelected(!isSelected)
          }}
        >
          {skillName} ({charType})
        </span>
      </div>
      <div className='career' onClick={async () => {
        const newCareerList = {}
        newCareerList[skillIndex] = !isCareer
        onCareerSkill(newCareerList)
      }}>
        {isCareer &&
          <div className='pip' />
        }
      </div>
      <div className='ranks'>
        {times(5).map((_ ,idx) => {
          const isFilled = idx < ranks

          const onClick = async () => {
            const newSkillRanks = {}
            newSkillRanks[skillIndex] = (isFilled && idx === 0) ? 0 : idx + 1
            await onSkillRanks(newSkillRanks)
          }

          return (
            <div
              key={idx}
              className={`rank${isFilled ? ' filled' : ''}`}
              onClick={onClick} />
          )
        })}
      </div>
    </div>
  )
}

const SkillSection = ({ campaignId, characterId, careerList, characteristics, setDicePool, skills, skillRanks, type }) => {
  const onCareerSkill = async (list) => await updateCareerSkill(campaignId, characterId, list)
  const onSkillRanks = async (ranks) => await updateSkillRanks(campaignId, characterId, ranks)

  const onDicePool = (dice, skillIndex) => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    setDicePool(dice, skillIndex)
  }

  return (
    <>
      <div css={`display: flex; justify-content: center; margin: 12px 0 8px; font-weight: bold;`}>
        <div css={`flex: 1; text-align: left;`}>{type}</div>
        <div>Career?</div>
        <div css={`flex: 1; text-align: right;`}>Rank</div>
      </div>
      <div className='section'>
        {skills.map((skillIndex) => {
          const skillName = SKILL_KEYS[skillIndex]
          const isCareer = careerList[skillIndex]
          const ranks = skillRanks[skillIndex] || 0
          const characteristic = parseInt(characteristics[SKILLS[skillName]], 10)

          return (
            <Skill
              key={skillIndex}
              characteristic={characteristic}
              isCareer={isCareer}
              onCareerSkill={onCareerSkill}
              onDicePool={onDicePool}
              onSkillRanks={onSkillRanks}
              skillIndex={skillIndex}
              skillName={skillName}
              ranks={ranks} />
          )
        })}
      </div>
    </>
  )
}

const Skills = ({ campaignId, characterId, careerList = {}, details = {}, setDicePool, skillRanks = {}, titleClass }) => {
  const theme = useTheme()
  const { chars: characteristics = {} } = details

  const baseProps = {
    campaignId, characterId, characteristics, careerList, details, skillRanks, setDicePool
  }

  const isDarkMode = theme.palette.type === 'dark'
  const mainColor = isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'

  const { isOpen, expander } = usePaperExpand('skills')

  const title = (
    <div className={titleClass} css={`display: flex; margin-bottom: ${!isOpen && '0'};`}>
      {expander}
      Skills
    </div>
  )

  if (!isOpen) return title

  return (
    <>
      <div css={`display: flex; justify-content: space-between;`}>
        {title}
        <div css={`display: flex; align-items: center; margin-bottom: 16px; font-size: 11px; text-align: right; line-height: 1.1;`}>
          Click a skill name to select difficulty and populate the dice pool.
        </div>
      </div>
      <div css={`
        .row {
          position: relative;
          padding: 4px 0;
          display: flex;
          justify-content: center;
        }
        .name {
          flex: 1;
          text-align: left;
          text-transform: capitalize;

          &.valid span {
            cursor: pointer;

            &:hover {
              font-weight: 500;
            }
          }
        }

        .career {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 18px;
          height: 18px;
          border: 1px solid ${mainColor};
          margin: 0 4px;
          cursor: pointer;

          &:hover {
            border-color: ${isDarkMode ? 'white' : 'black'};
            border-width: 2px;
          }

          .pip {
            border-radius: 50%;
            height: 14px;
            width: 14px;
            background-color: ${mainColor};
          }
        }
        .ranks {
          flex: 1;
          display: flex;
          justify-content: flex-end;
        }

        .rank {
          width: 20px;
          height: 18px;
          margin: 0 2px;
          border: 1px solid ${mainColor};
          cursor: pointer;

          &:hover {
            border-color: ${isDarkMode ? 'white' : 'black'};
            border-width: 2px;
          }

          &.filled {
            background-color: ${mainColor};
          }
        }
      `}>
        {/* General Skills */}
        <SkillSection skills={GENERAL} type='General' {...baseProps} />
        <SkillSection skills={COMBAT} type='Combat' {...baseProps} />
        <SkillSection skills={KNOWLEDGE} type='Knowledge' {...baseProps} />
      </div>
    </>
  )
}

export default Skills
